import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  calcularIct, comIct, formatarIct, ictEscrito, medidaVazia, medidasParaGravar,
  normalizarMedidas, semIct,
  type Calibracao, type MedidaDaImagem, type Medidas,
} from '../../../compartilhado/medidas.ts';
import { normalizarOit, oitVazio, pendenciasOit, type DadosOit } from '../../../compartilhado/oit.ts';
import { FolhaOit } from '../componentes/FolhaOit.tsx';
import { ReguaIct } from '../componentes/ReguaIct.tsx';
import { useClinica } from '../componentes/Marca.tsx';
import { Cabecalho, Layout } from '../componentes/Layout.tsx';
import { Area, Aviso, Botao, Chapa, Selecao, Selo, type Estagio } from '../componentes/forma.tsx';
import { api, ErroApi } from '../lib/api.ts';
import { useOpcoes } from '../lib/cadastro.ts';
import { dataBr, idade } from '../lib/formato.ts';
import { useUsuario } from '../lib/sessao.tsx';
import './laudo.css';

interface Contexto {
  exame: {
    id: string;
    codigo: number;
    dataExame: string;
    situacao: Estagio;
    pacienteNome: string;
    pacienteNascimento: string;
    procedimentoNome: string;
    tipoLaudo: 'TEXTO' | 'OIT_2000';
    modeloLaudo: string | null;
    empresaNome: string | null;
    convenioNome: string | null;
    funcao: string | null;
    assinadoEm: string | null;
  };
  laudo: {
    id: string;
    medicoId: string;
    tipo: 'TEXTO' | 'OIT_2000';
    conteudoHtml: string | null;
    dadosOit: unknown;
    /** Marcas da régua, por id de imagem. Ver compartilhado/medidas.ts. */
    medidas: unknown;
    assinadoEm: string | null;
    medicoNome: string | null;
    medicoCrm: string | null;
    medicoTemAssinatura: boolean;
    /** Veio do sistema antigo: existe como PDF pronto e nada mais. */
    legado: boolean;
  } | null;
  imagens: {
    id: string;
    ordem: number;
    titulo: string | null;
    /** Largura da chapa de laudo e milimetros por pixel. Os dois juntos, ou
     *  nenhum: um sem o outro nao vira centimetro. */
    larguraPx: number | null;
    mmPorPixel: number | null;
  }[];
  clinica: { nome: string; endereco: string; telefone: string } | null;
}

export function Laudo() {
  const { exameId } = useParams<{ exameId: string }>();
  const navegar = useNavigate();
  const qc = useQueryClient();
  const opcoes = useOpcoes();
  const marca = useClinica();
  const eu = useUsuario();

  const [oit, setOit] = useState<DadosOit>(oitVazio);
  /* As marcas da régua ficam aqui, e não dentro da chapa: são parte do laudo.
     Entram no mesmo rascunho, no mesmo salvamento automático e na mesma versão
     assinada que o texto -- a chapa só empresta a tela para marcá-las. */
  const [medidas, setMedidas] = useState<Medidas>({});
  const [recusando, setRecusando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [texto, setTexto] = useState('');
  const [medicoId, setMedicoId] = useState('');
  const [erro, setErro] = useState('');
  const [salvoEm, setSalvoEm] = useState<string | null>(null);

  const ctx = useQuery({
    queryKey: ['laudo', exameId],
    queryFn: ({ signal }) => api.get<Contexto>(`/laudos/exame/${exameId}`, signal),
  });

  useEffect(() => {
    const d = ctx.data;
    if (!d) return;
    setOit(normalizarOit(d.laudo?.dadosOit));
    setMedidas(normalizarMedidas(d.laudo?.medidas));
    // Sem laudo ainda: o editor abre com o modelo do procedimento, que é o
    // texto que a clínica já usa para o exame sem alteração.
    setTexto(d.laudo?.conteudoHtml ?? d.exame.modeloLaudo ?? '');
    setMedicoId(d.laudo?.medicoId ?? '');
  }, [ctx.data]);

  /* Devolver o exame para a clínica. Não é cancelar nem laudar: é dizer que
     aquela imagem não dá para ler, e por quê. */
  const recusar = useMutation({
    mutationFn: () => api.post('/laudos/recusar', { exameId, medicoId, motivo: motivo.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['exames'] });
      navegar('/laudos');
    },
    onError: (e) =>
      setErro(e instanceof ErroApi ? (e.campos.motivo ?? e.message) : 'Não foi possível recusar.'),
  });

  const tipo = ctx.data?.exame.tipoLaudo ?? 'TEXTO';
  const assinado = !!ctx.data?.laudo?.assinadoEm;
  /* O médico solicitante abre esta tela para acompanhar o exame que pediu, não
     para laudar. Ele não assina e não recusa -- o servidor já recusa essas
     ações, mas a tela não pode oferecê-las e deixar que ele descubra pelo erro.
     A clínica (papel 'usuario') continua acompanhando normalmente. */
  const soLeitura = eu.papel === 'medico' && eu.podeLaudar === false;

  /* A régua é de quem lauda, e de mais ninguém -- foi assim que a clínica
     pediu. Quem está no balcão continua vendo a chapa igual, sem a ferramenta:
     medida sobre radiografia é leitura, e leitura é do médico. */
  const podeMedir = !assinado && !soLeitura && eu.papel === 'medico';

  /* O índice vai para onde o laudo é escrito: no texto livre, ou nos
     comentários da folha OIT -- é o único campo de texto dela, e é onde o
     médico já põe o que não cabe nos quadrados. */
  const escreverIctNoLaudo = (ict: number) => {
    if (tipo === 'OIT_2000') setOit((d) => ({ ...d, comentarios: comIct(d.comentarios, ict) }));
    else setTexto((t) => comIct(t, ict));
  };

  const tirarIctDoLaudo = () => {
    if (tipo === 'OIT_2000') setOit((d) => ({ ...d, comentarios: semIct(d.comentarios) }));
    else setTexto((t) => semIct(t));
  };

  /* O que está escrito no laudo agora. Serve para a régua de cada chapa saber
     se é o índice DELA que está lá -- e nascer com a opção marcada ao reabrir
     o exame, em vez de dizer que não escreveu o que escreveu. */
  const ictNoLaudo = ictEscrito(tipo === 'OIT_2000' ? oit.comentarios : texto);

  const corpo = () => ({
    exameId: exameId!,
    medicoId,
    tipo,
    conteudoHtml: tipo === 'TEXTO' ? texto : null,
    dadosOit: tipo === 'OIT_2000' ? (oit as unknown as Record<string, unknown>) : null,
    medidas: medidasParaGravar(medidas),
  });

  const salvar = useMutation({
    mutationFn: () => api.put('/laudos', corpo()),
    onSuccess: () => setSalvoEm(new Date().toLocaleTimeString('pt-BR').slice(0, 5)),
    onError: (e) => setErro(e instanceof ErroApi ? e.message : 'Não foi possível salvar.'),
  });

  const assinar = useMutation({
    mutationFn: () => api.post<{ laudoId: string; codigo: number; erro?: string }>('/laudos/assinar', corpo()),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['exames'] });
      void qc.invalidateQueries({ queryKey: ['laudo', exameId] });
      if (r.erro) setErro(r.erro);
      else window.open(`/api/laudos/${r.laudoId}/pdf`, '_blank');
    },
    onError: (e) => setErro(e instanceof ErroApi ? e.message : 'Não foi possível assinar.'),
  });

  // Rascunho salvo sozinho: a leitura é interrompida o tempo todo, e perder o
  // que já foi marcado é o tipo de coisa que faz desconfiar do sistema.
  useEffect(() => {
    // `soLeitura` também barra aqui: sem isso, o médico solicitante que só abriu
    // o exame para olhar veria um erro de salvamento sozinho na tela, quatro
    // segundos depois, sem ter feito nada.
    if (assinado || soLeitura || !medicoId || ctx.isPending) return;
    const t = setTimeout(() => salvar.mutate(), 4000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oit, texto, medidas, medicoId, assinado, soLeitura]);

  if (ctx.isPending) {
    return (
      <Layout>
        <Cabecalho titulo="Laudo" />
        <p className="vazio">Carregando…</p>
      </Layout>
    );
  }
  if (ctx.error || !ctx.data) {
    return (
      <Layout>
        <Cabecalho titulo="Laudo" />
        <div className="cartao vazio">
          <p className="vazio__titulo">Não foi possível abrir o laudo</p>
          <p>{ctx.error instanceof ErroApi ? ctx.error.message : 'Tente de novo.'}</p>
        </div>
      </Layout>
    );
  }

  const { exame, imagens, clinica, laudo } = ctx.data;
  const faltas = tipo === 'OIT_2000' ? pendenciasOit(oit) : [];
  const semImagem = imagens.length === 0;
  const medico = opcoes.data?.medicos.find((m) => m.id === medicoId);

  return (
    <Layout>
      <Cabecalho titulo={`Laudo do exame ${exame.codigo}`}>
        <Botao variante="neutro" onClick={() => navegar(`/exames/${exame.id}`)}>
          Ver o exame
        </Botao>
        {assinado && laudo && (
          <Botao variante="primario" onClick={() => window.open(`/api/laudos/${laudo.id}/pdf`, '_blank')}>
            Abrir o PDF
          </Botao>
        )}
      </Cabecalho>

      <div className="laudo">
        <div className="laudo__folha">
          <header className="laudo__barra cartao">
            <Chapa codigo={exame.codigo} estagio={exame.situacao} />
            <Selo estagio={exame.situacao} />
            <span className="laudo__paciente">
              {exame.pacienteNome} · {idade(exame.pacienteNascimento)} anos ·{' '}
              {dataBr(exame.dataExame)}
            </span>
            <span className="laudo__salvo">
              {assinado
                ? `Assinado em ${dataBr((laudo?.assinadoEm ?? '').slice(0, 10))}`
                : salvar.isPending
                  ? 'Salvando…'
                  : salvoEm
                    ? `Rascunho salvo ${salvoEm}`
                    : ''}
            </span>
          </header>

          {erro && <Aviso>{erro}</Aviso>}
          {/* Só faz sentido antes da assinatura. Exame migrado do WX3 vem com o
              laudo pronto e sem imagem no sistema -- dizer que ele "só pode ser
              assinado depois que a radiografia chegar" seria falso: ele já foi
              assinado, e a chapa está dentro do próprio PDF. */}
          {semImagem && !assinado && (
            <Aviso tipo="info">
              Este exame ainda não tem imagem. O laudo só pode ser assinado depois que a
              radiografia chegar da sala de RX.
            </Aviso>
          )}

          {/* Laudo do sistema antigo: o documento é o PDF, e não há formulário
              por trás dele. Mostrar a folha OIT em branco aqui daria a
              entender que o laudo está incompleto -- e depois da migração isto
              vale para todo exame anterior ao sistema novo. */}
          {ctx.data?.laudo?.legado ? (
            <div className="cartao laudo__legado">
              <p className="laudo__legado-titulo">Laudo do sistema antigo</p>
              <p>
                Este exame foi laudado antes desta versão do sistema. O documento assinado
                está guardado como PDF — é o mesmo arquivo que a clínica sempre entregou.
                {!ctx.data.laudo.medicoNome &&
                  ' O médico que assinou não consta no cadastro atual; o nome dele está no próprio documento.'}
              </p>
              <a
                className="botao botao--primario"
                href={`/api/laudos/exame/${exameId}/pdf`}
                target="_blank"
                rel="noreferrer"
              >
                Abrir o laudo em PDF
              </a>
            </div>
          ) : tipo === 'OIT_2000' ? (
            <div className="laudo__papel">
              <FolhaOit
                dados={oit}
                aoMudar={setOit}
                somenteLeitura={assinado || soLeitura}
                cabecalho={{
                  funcionario: exame.pacienteNome,
                  empresa: exame.empresaNome ?? '',
                  dataRx: dataBr(exame.dataExame),
                  dataLeitura: dataBr(
                    assinado ? (laudo?.assinadoEm ?? '').slice(0, 10) : new Date().toISOString().slice(0, 10),
                  ),
                  clinica: clinica?.nome ?? '',
                  // A folha na tela tem de ser a mesma que sai no PDF: o médico
                  // assina o que está vendo. Aqui a logo vem pela rota pública,
                  // que já está no navegador dele.
                  logo: marca.data?.temLogo ? '/api/publico/logo' : null,
                  rodape: clinica ? `${clinica.nome} - ${clinica.endereco} - Tel:${clinica.telefone}` : '',
                }}
                assinatura={
                  // Laudo sem médico identificado só existe entre os legados, e
                  // legado nem chega a renderizar a folha -- mas o tipo tem de
                  // dar conta do caso, senão a tela quebra em vez de avisar.
                  assinado && laudo
                    ? {
                        url: laudo.medicoId ? `/api/medicos/${laudo.medicoId}/assinatura` : null,
                        nome: laudo.medicoNome ?? '',
                        crm: laudo.medicoCrm ?? '',
                      }
                    : undefined
                }
              />
            </div>
          ) : (
            <div className="cartao laudo__editor">
              <h2 className="laudo__editor-titulo">{exame.procedimentoNome}</h2>
              <textarea
                className="entrada laudo__texto"
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                disabled={assinado || soLeitura}
                rows={16}
                placeholder="Descreva os achados. Uma linha por achado."
              />
              <p className="campo__dica">
                {exame.modeloLaudo
                  ? 'Começou pelo modelo do procedimento. Ajuste o que for diferente.'
                  : 'Este procedimento não tem modelo de laudo cadastrado.'}
              </p>
            </div>
          )}

          {!semImagem && (
            <section className="laudo__chapas">
              {imagens.map((im, i) => (
                <Radiografia
                  key={im.id}
                  id={im.id}
                  titulo={`Imagem ${i + 1} de ${imagens.length}`}
                  medida={medidas[im.id] ?? medidaVazia()}
                  calibracao={{ mmPorPixel: im.mmPorPixel, larguraPx: im.larguraPx }}
                  podeMedir={podeMedir}
                  jaNoLaudo={combina(ictNoLaudo, calcularIct(medidas[im.id]))}
                  aoEscreverNoLaudo={escreverIctNoLaudo}
                  aoTirarDoLaudo={tirarIctDoLaudo}
                  aoMudar={(m) =>
                    setMedidas((todas) => {
                      const proximo = { ...todas };
                      // Chapa sem marca sai do mapa em vez de ficar como um
                      // objeto de nulos: apagar tem de devolver ao estado de
                      // quem nunca mediu, inclusive no banco.
                      if (m.cardiaco || m.toracico) proximo[im.id] = m;
                      else delete proximo[im.id];
                      return proximo;
                    })
                  }
                />
              ))}
            </section>
          )}
        </div>

        <aside className="laudo__lado">
          <div className="cartao laudo__assinar">
            <p className="rotulo">Assinatura</p>
            <Selecao
              rotulo="Médico que assina"
              value={medicoId}
              onChange={(e) => setMedicoId(e.target.value)}
              disabled={assinado || soLeitura}
            >
              <option value="">Selecione</option>
              {opcoes.data?.medicos.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nome} — {m.crm}
                </option>
              ))}
            </Selecao>

            {soLeitura && !assinado && (
              <p className="campo__dica">
                Este exame ainda não foi laudado. Você recebe um aviso quando o
                laudo ficar pronto.
              </p>
            )}

            {!assinado && !soLeitura && (
              <>
                {faltas.length > 0 && (
                  <div className="laudo__faltas">
                    <p className="rotulo">Falta preencher</p>
                    <ul>
                      {faltas.map((f) => (
                        <li key={f}>{f}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <Botao
                  variante="primario"
                  carregando={assinar.isPending}
                  disabled={!medicoId || faltas.length > 0 || semImagem}
                  onClick={() => {
                    setErro('');
                    if (confirm(`Assinar o laudo do exame ${exame.codigo}? Depois disso não é possível alterar.`)) {
                      assinar.mutate();
                    }
                  }}
                >
                  {assinar.isPending ? 'Assinando…' : 'Laudar e gerar o PDF'}
                </Botao>
                <p className="campo__dica">
                  {medico && !opcoes.data?.medicos.length
                    ? ''
                    : 'Ao laudar, o exame é finalizado e o PDF é gerado com as imagens.'}
                </p>

                {/* Recusar fica embaixo e sem cor de destaque: é a saída para
                    quando a imagem não serve, não uma alternativa a laudar. */}
                <div className="laudo__recusa">
                  {!recusando ? (
                    <Botao
                      variante="silencioso"
                      tamanho="sm"
                      disabled={semImagem}
                      onClick={() => setRecusando(true)}
                    >
                      Recusar o exame
                    </Botao>
                  ) : (
                    <>
                      <Area
                        rotulo="Por que está recusando"
                        rows={3}
                        value={motivo}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMotivo(e.target.value)}
                        dica="A recepção lê este texto na fila. Diga o que precisa ser refeito."
                      />
                      <div className="laudo__recusa-acoes">
                        <Botao
                          variante="perigo"
                          tamanho="sm"
                          carregando={recusar.isPending}
                          disabled={!medicoId || motivo.trim().length < 10}
                          onClick={() => recusar.mutate()}
                        >
                          Devolver para a clínica
                        </Botao>
                        <Botao
                          variante="silencioso"
                          tamanho="sm"
                          onClick={() => {
                            setRecusando(false);
                            setMotivo('');
                          }}
                        >
                          Cancelar
                        </Botao>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>

        </aside>
      </div>
    </Layout>
  );
}

/* Uma chapa embaixo do formulário, na largura dele.
 *
 * O <img> quebrado do navegador não serve aqui: o médico precisa saber que a
 * imagem existe no exame mas não pôde ser carregada -- é diferente de o exame
 * não ter imagem, e é ele quem vai decidir se assina assim mesmo. */
/** O índice escrito no laudo é o desta chapa? Compara pelo texto do número,
 *  e não pelo valor: é o texto que está escrito lá, e 0,4649 e 0,4651 são a
 *  mesma linha para quem lê o laudo. */
function combina(escrito: number | null, daChapa: number | null): boolean {
  if (escrito === null || daChapa === null) return false;
  return formatarIct(escrito) === formatarIct(daChapa);
}

function Radiografia({
  id,
  titulo,
  medida,
  aoMudar,
  calibracao,
  podeMedir,
  jaNoLaudo,
  aoEscreverNoLaudo,
  aoTirarDoLaudo,
}: {
  id: string;
  titulo: string;
  medida: MedidaDaImagem;
  aoMudar: (m: MedidaDaImagem) => void;
  calibracao: Calibracao;
  podeMedir: boolean;
  jaNoLaudo: boolean;
  aoEscreverNoLaudo: (ict: number) => void;
  aoTirarDoLaudo: () => void;
}) {
  const [falhou, setFalhou] = useState(false);

  return (
    <figure className="laudo__chapa">
      {falhou ? (
        <p className="laudo__chapa-falha">
          Não foi possível carregar esta imagem. Ela consta no exame, mas o arquivo não
          respondeu — avise a clínica antes de assinar.
        </p>
      ) : (
        /* A imagem deixou de ser um link: o clique agora marca a medida. O
           tamanho cheio continua a um clique, no painel embaixo da chapa, para
           quem mede e para quem só está olhando. */
        <ReguaIct
          src={`/api/imagens/${id}/web`}
          cheia={`/api/imagens/${id}/laudo`}
          descricao={titulo}
          medida={medida}
          aoMudar={aoMudar}
          calibracao={calibracao}
          podeMedir={podeMedir}
          jaNoLaudo={jaNoLaudo}
          aoEscreverNoLaudo={aoEscreverNoLaudo}
          aoTirarDoLaudo={aoTirarDoLaudo}
          aoFalhar={() => setFalhou(true)}
        />
      )}
      <figcaption>
        <span>{titulo}</span>
      </figcaption>
    </figure>
  );
}
